import { AdaptiveLevel } from './level'
import { VoiceError } from '../types'

/**
 * Owns the microphone: the MediaStream, the AudioContext, and the loop that
 * turns incoming audio into a level for the orb.
 *
 * There is exactly one of these per session. Transports borrow it rather than
 * each opening their own stream, because a second `getUserMedia` call on a
 * phone can steal the first one's audio route and leave the orb reading zero
 * while the recogniser hears fine.
 */

export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unknown'

export interface MicrophoneOptions {
  /**
   * Speech-tuned constraints. Echo cancellation matters most here: without it
   * the assistant's own voice comes back through the microphone and reads as
   * the user interrupting, so the assistant cuts itself off mid-sentence.
   */
  constraints?: MediaTrackConstraints
  /** Called on every animation frame with a smoothed 0..1 level. */
  onLevel?: (level: number) => void
}

/**
 * Every constraint is `ideal`, never `exact`. An exact `sampleRate` or
 * `channelCount` throws `OverconstrainedError` on a lot of Android hardware,
 * and iOS ignores a requested sample rate anyway and hands back the hardware
 * session rate. Ask, then read `track.getSettings()` for what actually
 * happened.
 */
export const SPEECH_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: { ideal: true },
  noiseSuppression: { ideal: true },
  autoGainControl: { ideal: true },
  channelCount: { ideal: 1 },
}

/**
 * Tells Safari this is a two-way voice call rather than media playback.
 * Without it, the hardware ringer switch silences the assistant and the user
 * gets a mute orb with no explanation. Safari 16.4 and up only, so it is
 * feature-detected and failure is ignored.
 */
function configureAudioSession(): void {
  const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession
  if (!session) return
  try {
    session.type = 'play-and-record'
  } catch {
    // Older WebKit exposes the object but rejects some values.
  }
}

export function isMicrophoneSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof window !== 'undefined' &&
    (typeof window.AudioContext === 'function' ||
      typeof (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext ===
        'function')
  )
}

/**
 * Reads the permission state without triggering a prompt, so the app can show
 * the right screen before asking. Not every browser implements the microphone
 * descriptor, hence 'unknown' rather than a guess.
 */
export async function queryMicPermission(): Promise<MicPermission> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unknown'
  try {
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    })
    return status.state as MicPermission
  } catch {
    return 'unknown'
  }
}

function getAudioContextConstructor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null
  const scoped = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }
  return scoped.AudioContext ?? scoped.webkitAudioContext ?? null
}

function toVoiceError(error: unknown): VoiceError {
  const name = error instanceof Error ? error.name : ''
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new VoiceError('mic-denied', 'Microphone permission was refused.', {
        recoverable: false,
        cause: error,
      })
    case 'NotFoundError':
    case 'OverconstrainedError':
      return new VoiceError('mic-missing', 'No microphone is available.', {
        recoverable: false,
        cause: error,
      })
    case 'NotReadableError':
    case 'AbortError':
      return new VoiceError('mic-busy', 'The microphone is in use by something else.', {
        cause: error,
      })
    default:
      return new VoiceError('unknown', 'The microphone could not be opened.', { cause: error })
  }
}

export class Microphone {
  private stream: MediaStream | null = null
  private context: AudioContext | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private analyser: AnalyserNode | null = null
  // Explicitly backed by an ArrayBuffer: `getFloatTimeDomainData` refuses a
  // view that might sit on a SharedArrayBuffer.
  private buffer: Float32Array<ArrayBuffer> | null = null
  private frame: number | null = null
  private lastFrameAt = 0
  private readonly meter = new AdaptiveLevel()
  private readonly listeners = new Set<(level: number) => void>()
  private muted = false
  /** In-flight open, so overlapping calls share one getUserMedia. */
  private opening: Promise<void> | null = null
  private readonly constraints: MediaTrackConstraints

  constructor(options: MicrophoneOptions = {}) {
    this.constraints = { ...SPEECH_CONSTRAINTS, ...options.constraints }
    if (options.onLevel) this.listeners.add(options.onLevel)
  }

  get isOpen(): boolean {
    return this.stream !== null
  }

  get mediaStream(): MediaStream | null {
    return this.stream
  }

  get isMuted(): boolean {
    return this.muted
  }

  subscribe(listener: (level: number) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Opens the stream and starts metering. Must be called from a user gesture:
   * iOS will not let an AudioContext start any other way, and a context that
   * starts suspended reports a flat zero level forever with no error.
   */
  async open(): Promise<void> {
    // Re-entrancy guard, not just a null check.
    //
    // The old `if (this.stream) return` sat before the await, and the
    // assignment landed after it, so two overlapping calls both reached
    // getUserMedia. A double-tapped Retry button on a slow device did exactly
    // that: the second stream overwrote the first, `close()` stopped only the
    // second one's tracks, and the phone's recording indicator stayed lit long
    // after the user had left the app.
    if (this.opening) return this.opening
    if (this.stream) return

    this.opening = this.doOpen().finally(() => {
      this.opening = null
    })
    return this.opening
  }

  private async doOpen(): Promise<void> {
    if (this.stream) return

    if (!isMicrophoneSupported()) {
      throw new VoiceError('unsupported', 'This browser cannot capture audio.', {
        recoverable: false,
      })
    }

    configureAudioSession()

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: this.constraints, video: false })
    } catch (error) {
      throw toVoiceError(error)
    }

    if (this.stream) {
      // Something else won the race while this call was awaiting. Stop the
      // stream nobody is going to use rather than leaking a live microphone.
      for (const track of stream.getTracks()) track.stop()
      return
    }

    this.stream = stream
    this.applyMuteToTracks()

    const AudioContextCtor = getAudioContextConstructor()
    if (!AudioContextCtor) return

    try {
      // Built after getUserMedia resolves, deliberately. Opening the microphone
      // changes the system audio session and can change the hardware sample
      // rate, and a context created beforehand can end up mismatched. The rate
      // is never pinned for the same reason.
      this.context = new AudioContextCtor()
      if (this.context.state === 'suspended') await this.context.resume()

      this.source = this.context.createMediaStreamSource(stream)
      this.analyser = this.context.createAnalyser()
      // 1024 gives ~21ms of audio at 48kHz: fine enough to follow syllables,
      // coarse enough that the per-frame maths stays cheap on a low-end phone.
      this.analyser.fftSize = 1024
      this.analyser.smoothingTimeConstant = 0.75
      this.buffer = new Float32Array(this.analyser.fftSize)
      this.source.connect(this.analyser)
      // Deliberately not connected to the destination: routing the microphone
      // to the speaker would feed back.
      this.startMetering()
    } catch (error) {
      // Metering is a nicety. If the audio graph fails the session can still
      // run, so this degrades to a flat orb instead of a dead app.
      console.warn('Namu: microphone metering unavailable', error)
      this.teardownAudioGraph()
    }
  }

  private startMetering(): void {
    if (this.frame !== null) return
    if (typeof requestAnimationFrame !== 'function') return

    const SILENT_FRAME = new Float32Array(1)
    this.lastFrameAt = 0

    const tick = (timestamp: number) => {
      const analyser = this.analyser
      const buffer = this.buffer
      if (!analyser || !buffer) return

      // Real elapsed time, not an assumed 60fps. A mid-range phone under load
      // drops to 30fps, and a fixed dt would make the meter twice as sluggish
      // exactly when the device is already struggling.
      const dt = this.lastFrameAt === 0 ? 1 / 60 : Math.min(0.1, (timestamp - this.lastFrameAt) / 1000)
      this.lastFrameAt = timestamp

      analyser.getFloatTimeDomainData(buffer)
      const level = this.meter.push(this.muted ? SILENT_FRAME : buffer, dt)
      for (const listener of this.listeners) listener(level)

      this.frame = requestAnimationFrame(tick)
    }

    this.frame = requestAnimationFrame(tick)
  }

  private stopMetering(): void {
    if (this.frame !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.frame)
    }
    this.frame = null
    this.lastFrameAt = 0
    this.meter.reset()
    for (const listener of this.listeners) listener(0)
  }

  /** The room's current noise floor in dBFS. Surfaced in diagnostics. */
  get noiseFloorDb(): number {
    return this.meter.noiseFloorDb
  }

  /**
   * Muting disables the tracks rather than closing the stream, so the recording
   * indicator behaviour stays honest and unmuting is instant. Closing and
   * reopening would cost a second of route negotiation on Android.
   */
  setMuted(muted: boolean): void {
    this.muted = muted
    this.applyMuteToTracks()
    if (muted) {
      this.meter.reset()
      for (const listener of this.listeners) listener(0)
    }
  }

  private applyMuteToTracks(): void {
    if (!this.stream) return
    for (const track of this.stream.getAudioTracks()) track.enabled = !this.muted
  }

  /** Resumes a context the OS suspended, e.g. after the app was backgrounded. */
  async resume(): Promise<void> {
    if (this.context && this.context.state === 'suspended') {
      try {
        await this.context.resume()
      } catch (error) {
        console.warn('Namu: could not resume audio context', error)
      }
    }
  }

  private teardownAudioGraph(): void {
    this.stopMetering()
    try {
      this.source?.disconnect()
      this.analyser?.disconnect()
    } catch {
      // Disconnecting an already-torn-down node is not worth reporting.
    }
    this.source = null
    this.analyser = null
    this.buffer = null
    if (this.context && this.context.state !== 'closed') void this.context.close()
    this.context = null
  }

  close(): void {
    this.teardownAudioGraph()
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop()
      this.stream = null
    }
    this.muted = false
  }
}
